import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, SpinningLoader } from "risa-oasis-ui_v2";
import { getFileDownloadUrl } from "../../../api/firebase/firestoreService";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import {
  DocumentsForOrder,
  DocumentsViewerPropsWithSideNavigation,
  Item,
} from "../../../data-model/documentModalWithSideBav";
import { EventsName } from "../../../enums/eventsName";
import { ModalId } from "../../../enums/modalId";
import { closeModal } from "../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import ChevronDown from "../../../svg/chevron-down";
import { InfoIcon } from "../../../svg/info-icon";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import { LoaderMessage } from "../../loaderMessage/loaderMessage";
import PdfRender from "../../pdfRender/pdfRender";

interface SideNavElementProps {
  active?: boolean;
  item?: Item;
  onSelect?: (item: Item) => void;
}

interface DocViewerProps {
  doc: DocumentsForOrder;
  onPrevious?: () => void;
  onNext?: () => void;
}

const DocViewerWithSideNavigation = (
  props: DocumentsViewerPropsWithSideNavigation,
) => {
  const { metaData } = useSelector((state: RootState) => state.modal);
  const { items } = metaData as DocumentsViewerPropsWithSideNavigation;
  const [selectedNavItem, setSelectedNavItem] = useState<string>("");
  const [selectedDocIndex, setSelectedDocIndex] = useState<number>(0);
  const [allDocs, setAllDocs] = useState<DocumentsForOrder[]>([]);
  const dispatch = useDispatch<AppDispatch>();
  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const orgID = getOrgIdForFetchExternalWorklist();
  const isEventLogged = useRef(false);

  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const itemsWithDocuments = useMemo(() => {
    return items?.filter(
      (item) => item?.documents && item?.documents?.length > 0,
    );
  }, [items]);

  useEffect(() => {
    if (
      itemsWithDocuments &&
      itemsWithDocuments.length > 0 &&
      !props?.showLoader
    ) {
      setSelectedNavItem(itemsWithDocuments[0].id ?? "");
      setSelectedDocIndex(0);
      setAllDocs(itemsWithDocuments[0].documents ?? []);
      if (props?.shouldLogEvent && !isEventLogged.current) {
        apiWorkerManager.enqueueAndProcess({
          patient_id: singleMedicalPaOrder?.patient_id ?? "",
          order_id: singleMedicalPaOrder?.order_id ?? "",
          org_id: orgID ?? "",
          event_name: EventsName.MODAL_SHOWN,
          user_id: user?.id ?? "",
          user_email: user?.email ?? "",
          additional_data: {
            screen_name: props?.screenName ?? "",
            modal_name:
              props?.modalName ?? ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION,
            payer_name: payerName,
          },
        });

        isEventLogged.current = true;
      }
    }
  }, [itemsWithDocuments, props?.showLoader]);

  useEffect(() => {
    if (selectedNavItem && !props?.showLoader) {
      isEventLogged.current &&
        props?.shouldLogEvent &&
        apiWorkerManager.enqueueAndProcess({
          patient_id: singleMedicalPaOrder?.patient_id ?? "",
          order_id: singleMedicalPaOrder?.order_id ?? "",
          org_id: orgID ?? "",
          event_name:
            EventsName.GUIDELINE_TAB_SELECTED + " (" + selectedNavItem + ")",
          user_id: user?.id ?? "",
          user_email: user?.email ?? "",
          additional_data: {
            screen_name: props?.screenName ?? "",
            j_code: selectedNavItem ?? "",
            modal_name:
              props?.modalName ?? ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION,
            payer_name: payerName,
          },
        });
    }
  }, [selectedNavItem, props?.showLoader]);

  useEffect(() => {
    if (
      allDocs &&
      allDocs.length > 0 &&
      isEventLogged.current &&
      props?.shouldLogEvent &&
      !props?.showLoader
    ) {
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: orgID ?? "",
        event_name:
          EventsName.GUIDELINE_VIEWED +
          " (" +
          allDocs?.[selectedDocIndex]?.title +
          ")",
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: props?.screenName ?? "",
          j_code: selectedNavItem ?? "",
          document_name: allDocs?.[selectedDocIndex]?.title ?? "",
          modal_name:
            props?.modalName ?? ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION,
          payer_name: payerName,
        },
      });
    }
  }, [selectedDocIndex, allDocs, props?.showLoader]);

  return (
    <Modal
      dialogId={ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION}
      onSave={function (): void | Promise<void> {
        throw new Error("Function not implemented.");
      }}
      title={"Documents"}
      saveButtonText={""}
      cancelText={""}
      hideFooter={true}
      heightPercentage={90}
      onClose={() => {
        dispatch(closeModal());
      }}
      onCancel={() => {
        dispatch(closeModal());
      }}
    >
      <div className="doc-viewer-with-side-navigation--container flex h-full gap-1 overflow-hidden">
        {props?.showLoader ? (
          <>
            <div className="loader-container flex h-full w-full items-center justify-center gap-4">
              <SpinningLoader />
              <span>Loading documents...</span>
            </div>
          </>
        ) : (
          <>
            {itemsWithDocuments && itemsWithDocuments.length > 0 ? (
              <>
                <div className="side-nav--container flex w-[25%] flex-col overflow-auto bg-[#F7F9FA]">
                  {itemsWithDocuments?.map(
                    (item) =>
                      item?.documents &&
                      item?.documents?.length > 0 && (
                        <SideNavElement
                          key={item.id}
                          active={item.id === selectedNavItem}
                          item={item}
                          onSelect={(item) => {
                            setSelectedNavItem(item.id ?? "");
                            setSelectedDocIndex(0);
                            setAllDocs(item.documents ?? []);
                          }}
                        />
                      ),
                  )}
                </div>
                <div className="doc-viewer--container flex w-[75%] flex-1 flex-col gap-1 overflow-hidden">
                  {allDocs?.[selectedDocIndex] && (
                    <DocViewer
                      doc={allDocs?.[selectedDocIndex] ?? {}}
                      onPrevious={() => {
                        setSelectedDocIndex(selectedDocIndex - 1);
                      }}
                      onNext={() => {
                        setSelectedDocIndex(selectedDocIndex + 1);
                      }}
                    />
                  )}
                </div>
              </>
            ) : (
              <NoDocumentsFound />
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

const SideNavElement = (props: SideNavElementProps) => {
  return (
    <div
      className={`side-nav-element--container h-fit w-full border-l-4 bg-primaryGray-16 py-1 ${
        props.active ? "border-tertiaryBlue-4" : "border-transparent"
      }`}
      onClick={() => props.onSelect?.(props?.item ?? {})}
    >
      {props.active ? (
        <div className="side-nav-element--active-content truncate bg-white p-2 text-small font-bold text-tertiaryBlue-4">
          {props.item?.description ?? ""}
        </div>
      ) : (
        <div className="side-nav-element--inactive cursor-pointer truncate p-2 text-small font-regular hover:bg-primaryGray-15 hover:font-semiBold">
          {props.item?.description ?? ""}
        </div>
      )}
    </div>
  );
};

const DocViewer = (props: DocViewerProps) => {
  const [docUrl, setDocUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchDocUrl = async () => {
    try {
      setIsLoading(true);
      const url = await getFileDownloadUrl(props?.doc?.path ?? "");
      setDocUrl(url ?? "");
    } catch (error) {
      console.error(error);
      setDocUrl("");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (props?.doc?.path) {
      fetchDocUrl();
    }
  }, [props?.doc?.path]);
  return (
    <>
      <div className="doc-viewer--header flex items-center justify-between border-b border-primaryGray-15 px-4 py-2 shadow-md">
        <div
          className="prev flex cursor-pointer items-center gap-1 text-sm font-bold text-tertiaryBlue-4"
          onClick={() => props?.onPrevious?.()}
        >
          {props?.doc?.showPrevious && (
            <>
              <ChevronDown rotate={90} stroke="#0056D6" />
              <span>Prev</span>
            </>
          )}
        </div>
        <div className="title text-small font-semibold">
          {props?.doc?.title ?? ""}
        </div>
        <div
          className="next flex cursor-pointer items-center gap-1 text-sm font-bold text-tertiaryBlue-4"
          onClick={() => props?.onNext?.()}
        >
          {props?.doc?.showNext && (
            <>
              <span>Next</span>
              <ChevronDown rotate={-90} stroke="#0056D6" />
            </>
          )}
        </div>
      </div>
      <div className="doc-viewer flex-1 overflow-auto px-2">
        {isLoading ? (
          <div className="flex h-full w-full items-center justify-center">
            <LoaderMessage message="" />
          </div>
        ) : (
          <PdfRender file={docUrl} hideRotateButtons hideZoomButtons />
        )}
      </div>
    </>
  );
};

export default DocViewerWithSideNavigation;

const NoDocumentsFound = () => {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2">
      <InfoIcon height="24" width="24" />
      <span className="text-sm font-semibold">No documents found</span>
    </div>
  );
};
